import { Form, Modal } from 'antd'
import dayjs from 'dayjs'
import { FC, useEffect } from 'react'
import useErrorModal from '../../../../hooks/api/use-error-modal'
import {
    useCreateShiftScheduleMutation,
    useUpdateShiftScheduleMutation,
} from '../../../../store/api/shift-schedules-api'
import ShiftSchedule, { ShiftScheduleStatusEnum } from '../../../../types/api/shift-schedule'
import ShiftType from '../../../../types/api/shift-type'
import { ShiftScheduleFormValues } from './form-values'
import ShiftScheduleForm from './shift-schedule-form'

interface ShiftScheduleModalProps {
    open: boolean
    shiftSchedule?: ShiftSchedule | null
    shiftTypes: ShiftType[]
    onClose: () => void
}

const getInitialValues = (shiftSchedule?: ShiftSchedule | null): Partial<ShiftScheduleFormValues> => ({
    date: dayjs(),
    employee: shiftSchedule?.employee?.documentId,
    shiftType: shiftSchedule?.shiftType?.documentId,
})

const formatDateForApi = (values: ShiftScheduleFormValues) => values.date.format('YYYY-MM-DD')

const buildCreatePayload = (values: ShiftScheduleFormValues) => ({
    date: formatDateForApi(values),
    employee: { connect: [values.employee] },
    shiftType: { connect: [values.shiftType] },
    plannedHours: 0,
    actualHours: 0,
    statusEnum: ShiftScheduleStatusEnum.Completed,
})

const buildUpdatePayload = (values: ShiftScheduleFormValues) => ({
    date: formatDateForApi(values),
    employee: { set: [values.employee] },
    shiftType: { set: [values.shiftType] },
    plannedHours: 0,
    actualHours: 0,
    statusEnum: ShiftScheduleStatusEnum.Completed,
})

const ShiftScheduleModal: FC<ShiftScheduleModalProps> = ({
    open,
    shiftSchedule,
    shiftTypes,
    onClose,
}) => {
    const [form] = Form.useForm<ShiftScheduleFormValues>()
    const [createShiftSchedule, createState] = useCreateShiftScheduleMutation()
    const [updateShiftSchedule, updateState] = useUpdateShiftScheduleMutation()
    const isEditing = Boolean(shiftSchedule)
    const isSaving = createState.isLoading || updateState.isLoading

    useErrorModal(createState, 'Ошибка создания смены')
    useErrorModal(updateState, 'Ошибка редактирования смены')

    useEffect(() => {
        if (!open) return

        form.setFieldsValue(getInitialValues(shiftSchedule))
    }, [form, open, shiftSchedule])

    const handleClose = () => {
        form.resetFields()
        onClose()
    }

    const handleOk = async () => {
        const values = await form.validateFields()

        if (shiftSchedule) {
            await updateShiftSchedule({
                docId: shiftSchedule.documentId,
                body: { data: buildUpdatePayload(values) },
            }).unwrap()
        } else {
            await createShiftSchedule({
                data: buildCreatePayload(values),
            }).unwrap()
        }

        handleClose()
    }

    return (
        <Modal
            title={isEditing ? 'Редактировать смену' : 'Добавить смену'}
            open={open}
            onCancel={handleClose}
            onOk={handleOk}
            confirmLoading={isSaving}
            okText='Сохранить'
            cancelText='Отмена'
        >
            <ShiftScheduleForm
                form={form}
                shiftTypeOptions={
                    shiftTypes.map(item => ({ value: item.documentId, label: item.name })) || []
                }
            />
        </Modal>
    )
}

export default ShiftScheduleModal
